import React, { useContext, useEffect, useMemo, useState } from "react";
import {
  Autocomplete,
  Box,
  Button,
  Divider,
  Grid,
  IconButton,
  Modal,
  Paper,
  TextField,
  Typography,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import Inventory2OutlinedIcon from "@mui/icons-material/Inventory2Outlined";
import { AppProvider } from "../../ContextApi/AppProvider";
import colors from "../../theme/colors";
import { formatToBRL } from "../../Utils/utils";
import Produto from "../Shared/Interfaces/Produto";
import type { KitResponseDto, KitUpsertRequestDto } from "../Shared/Interfaces/Kit";
import { EstoqueService } from "../../Services/EstoqueService";
import { KitsService } from "../../Services/KitsService";

const estoqueService = new EstoqueService();
const kitsService = new KitsService();

const styleModal = {
  position: "absolute" as const,
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 980,
  maxWidth: "96vw",
  maxHeight: "90vh",
  overflowY: "auto" as const,
  bgcolor: "background.paper",
  boxShadow: 24,
  p: 3,
  borderRadius: 1,
};

type ProdutoSelecionado = {
  produto: Produto | null;
  quantidade: number;
};

export interface ModalCadastrarKitProps {
  open: boolean;
  onClose: () => void;
  onKitSalvo?: () => void;
  kitEdicao?: KitResponseDto | null;
}

export default function ModalCadastrarKit({
  open,
  onClose,
  onKitSalvo,
  kitEdicao,
}: ModalCadastrarKitProps) {
  const contextApi = useContext(AppProvider);
  const [loading, setLoading] = useState(false);
  const [produtos, setProdutos] = useState<Produto[]>([]);

  const [nome, setNome] = useState("");
  const [descricao, setDescricao] = useState("");
  const [valor, setValor] = useState<number>(0);
  const [itens, setItens] = useState<ProdutoSelecionado[]>([
    { produto: null, quantidade: 1 },
  ]);

  const titulo = kitEdicao?.id ? "Editar Kit" : "Cadastrar Kit";

  useEffect(() => {
    if (!open) return;

    const carregarProdutos = async () => {
      try {
        const response = await estoqueService.getItems(
          "/api/Produtos/Buscar?MostrarZerados=true"
        );
        setProdutos(response.data || []);
      } catch (e) {
        setProdutos([]);
        contextApi.showMessage(
          "Erro ao carregar produtos para montar o kit.",
          true,
          "error"
        );
      }
    };

    carregarProdutos();

    if (kitEdicao) {
      setNome(kitEdicao.nome || "");
      setDescricao(kitEdicao.descricao || "");
      setValor(Number(kitEdicao.valor) || 0);
      setItens(
        (kitEdicao.produtos || []).map((p) => ({
          produto: null,
          quantidade: p.quantidade || 1,
        }))
      );
    } else {
      setNome("");
      setDescricao("");
      setValor(0);
      setItens([{ produto: null, quantidade: 1 }]);
    }
  }, [open, kitEdicao]);

  // Resolve os produtos do kit em edição (quando existir) após carregar a lista de produtos.
  useEffect(() => {
    if (!open || !kitEdicao) return;
    if (!produtos || produtos.length === 0) return;
    setItens(
      (kitEdicao.produtos || []).map((p) => ({
        produto: produtos.find((x) => x.id === p.idProduto) || null,
        quantidade: p.quantidade || 1,
      }))
    );
  }, [open, kitEdicao, produtos]);

  const produtosDisponiveis = useMemo(() => produtos || [], [produtos]);

  const onChangeValor = (raw: string) => {
    const unformattedValue = raw.replace(/\D/g, "");
    const numericValue = Number(unformattedValue) / 100;
    setValor(Number.isFinite(numericValue) ? numericValue : 0);
  };

  const adicionarLinha = () => {
    setItens((prev) => [...prev, { produto: null, quantidade: 1 }]);
  };

  const removerLinha = (idx: number) => {
    setItens((prev) => prev.filter((_, i) => i !== idx));
  };

  const atualizarLinha = (idx: number, next: Partial<ProdutoSelecionado>) => {
    setItens((prev) =>
      prev.map((item, i) => (i === idx ? { ...item, ...next } : item))
    );
  };

  const salvar = async () => {
    const payload: KitUpsertRequestDto = {
      nome: nome.trim(),
      descricao: descricao?.trim() ? descricao.trim() : null,
      valor: Number(valor) || 0,
      produtos: itens
        .filter((i) => i.produto && i.produto.id > 0 && i.quantidade > 0)
        .map((i) => ({
          idProduto: (i.produto as Produto).id,
          quantidade: Number(i.quantidade) || 0,
        })),
    };

    if (!payload.nome) {
      contextApi.showMessage("Informe o nome do kit.", true, "warning");
      return;
    }
    if (payload.valor <= 0) {
      contextApi.showMessage("Informe um valor válido para o kit.", true, "warning");
      return;
    }
    if (!payload.produtos || payload.produtos.length === 0) {
      contextApi.showMessage("Selecione ao menos 1 produto para o kit.", true, "warning");
      return;
    }

    setLoading(true);
    try {
      const response = kitEdicao?.id
        ? await kitsService.atualizar(kitEdicao.id, payload)
        : await kitsService.adicionar(payload);

      const ok = response.status === 201 || response.status === 204 || response.status === 200;
      if (ok) {
        contextApi.showMessage(
          kitEdicao?.id ? "Kit atualizado com sucesso!" : "Kit cadastrado com sucesso!",
          true,
          "success"
        );
        onKitSalvo?.();
        onClose();
      } else {
        contextApi.showMessage("Erro ao salvar o kit. Tente novamente.", true, "error");
      }
    } catch (error: any) {
      const msg =
        error?.response?.data != null
          ? typeof error.response.data === "string"
            ? error.response.data
            : JSON.stringify(error.response.data)
          : "Erro ao salvar kit.";
      contextApi.showMessage(msg, true, "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal open={open} onClose={() => !loading && onClose()}>
      <Box sx={styleModal}>
        <Typography
          variant="h6"
          sx={{
            mb: 0.5,
            color: colors.bluePrimary,
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          <Inventory2OutlinedIcon fontSize="small" />
          {titulo}
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Monte um kit com produtos do estoque e defina o valor final do kit.
        </Typography>

        <Paper elevation={0} sx={{ p: 0 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={5}>
              <TextField
                fullWidth
                label="Nome"
                size="small"
                value={nome}
                onChange={(e) => setNome(e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                fullWidth
                label="Descrição"
                size="small"
                value={descricao}
                onChange={(e) => setDescricao(e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                fullWidth
                label="Valor"
                size="small"
                value={formatToBRL(Number(valor) || 0)}
                onChange={(e) => onChangeValor(e.target.value)}
              />
            </Grid>
          </Grid>

          <Divider sx={{ my: 2 }} />

          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <Typography variant="subtitle2" sx={{ color: colors.bluePrimary }}>
              Produtos do kit
            </Typography>
            <Button
              variant="outlined"
              size="small"
              onClick={adicionarLinha}
              startIcon={<AddCircleOutlineIcon />}
              disabled={loading}
            >
              Adicionar produto
            </Button>
          </Box>

          <Grid container spacing={1.5} sx={{ mt: 0.5 }}>
            {itens.map((item, idx) => (
              <React.Fragment key={idx}>
                <Grid item xs={12} md={8}>
                  <Autocomplete
                    fullWidth
                    options={produtosDisponiveis}
                    getOptionLabel={(o) => `${o.descricao} (#${o.id})`}
                    value={item.produto}
                    onChange={(_, v) => atualizarLinha(idx, { produto: v })}
                    renderInput={(params) => (
                      <TextField {...params} label="Produto" size="small" />
                    )}
                  />
                </Grid>
                <Grid item xs={10} md={3}>
                  <TextField
                    fullWidth
                    label="Quantidade"
                    type="number"
                    size="small"
                    value={item.quantidade}
                    onChange={(e) =>
                      atualizarLinha(idx, {
                        quantidade: parseInt(e.target.value, 10) || 0,
                      })
                    }
                    inputProps={{ min: 1 }}
                  />
                </Grid>
                <Grid item xs={2} md={1} sx={{ display: "flex", justifyContent: "center" }}>
                  <IconButton
                    aria-label="remover"
                    onClick={() => removerLinha(idx)}
                    disabled={loading || itens.length === 1}
                    color="error"
                    size="small"
                    sx={{ mt: 0.5 }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Grid>
              </React.Fragment>
            ))}
          </Grid>
        </Paper>

        <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1, mt: 2 }}>
          <Button variant="outlined" onClick={onClose} disabled={loading}>
            Cancelar
          </Button>
          <Button variant="contained" onClick={salvar} disabled={loading}>
            Salvar
          </Button>
        </Box>
      </Box>
    </Modal>
  );
}

